import React from "react";
import ServicePageShell from "../ServicePageShell/ServicePageShell";
import { useIsDanish } from "../../i18n/LanguageContext";

const AzureGovernance = () => {
  const isDa = useIsDanish();
  const governanceServices = [
    {
      title: "Azure Policy (Definitions & Assignments)",
      desc: "Design and deploy policies that enforce standards automatically — no more “we’ll remember later”.",
      points: [
        "Allowed locations / SKUs",
        "Require tags (owner, costCenter, dataClass)",
        "Enforce private endpoints / TLS / HTTPS only",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 3 20 7v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V7l8-4Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M9 12l2 2 4-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "Initiatives (Policy Sets) & Baselines",
      desc: "Bundle policies into reusable baselines per environment, subscription, or workload type.",
      points: [
        "Platform baseline (landing zone)",
        "Workload baseline (apps / data / integration)",
        "Environment baselines (dev/pre/prd)",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M7 4h10a2 2 0 0 1 2 2v14H5V6a2 2 0 0 1 2-2Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M8 8h8M8 12h8M8 16h5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "Compliance, Remediation & Drift Control",
      desc: "Azure does not fix itself. We detect silent drift and correct it with safe, automated remediation.",
      points: [
        "Continuous compliance reporting",
        "Remediation tasks (where safe/possible)",
        "Prevent “click-ops” configuration drift",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M4 19V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M7 15l3-3 3 2 4-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M4 19h16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "Naming Conventions & Tagging Standards",
      desc: "Standardize naming and tags so your cloud is searchable, manageable, and cost-accountable.",
      points: [
        "Naming patterns per environment",
        "Mandatory tags for ownership + cost",
        "Consistent resource group structure",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M20 12v6a2 2 0 0 1-2 2H8l-4-4V6a2 2 0 0 1 2-2h6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M14 4h6v6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M20 4l-8 8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "RBAC & PIM Governance Model",
      desc: "Least privilege as a system: roles, groups, and PIM activation flows aligned to how teams actually work.",
      points: [
        "Role design & access boundaries",
        "PIM eligible groups + approvals",
        "Auditability & access reviews",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 21a8 8 0 0 1 16 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M18.5 7.5 21 10l-2.5 2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "Governance as Code (Bicep + Pipelines)",
      desc: "Everything versioned, reviewed, and deployed the same way as application code — repeatable and safe.",
      points: [
        "Bicep modules for policies/assignments",
        "Azure DevOps validate + what-if + deploy",
        "Change control & audit trail",
      ],
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M7 7 4 12l3 5M17 7l3 5-3 5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M14 6 10 18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
  ];

  const whyImportant = [
    {
      title: "Azure does not fix itself",
      desc: "If standards aren’t enforced continuously, things drift silently until security, cost, and reliability break.",
    },
    {
      title: "Silent drift is real",
      desc: "A single manual change can bypass conventions and create long-term risk (public exposure, wrong SKUs, missing tags).",
    },
    {
      title: "Consistency reduces risk",
      desc: "When every environment follows the same baseline, troubleshooting is faster and incidents are fewer.",
    },
    {
      title: "Compliance becomes a routine",
      desc: "Governance makes compliance measurable, repeatable, and auditable — not a yearly panic project.",
    },
  ];

  const useCases = [
    {
      title: "Prevent public exposure",
      desc: "Block public IPs or enforce private endpoints for sensitive workloads before it happens.",
    },
    {
      title: "Cost control and accountability",
      desc: "Require tags and enforce SKU/region rules to avoid surprise bills and unowned resources.",
    },
    {
      title: "Standardize landing zones at scale",
      desc: "Apply baselines consistently across subscriptions and environments (dev/pre/prd).",
    },
    {
      title: "Safe self-healing (where possible)",
      desc: "Auto-remediate noncompliant resources and alert when remediation needs human approval.",
    },
  ];

  const conventions = [
    "Clear naming convention per environment and workload",
    "Mandatory tags: owner, costCenter, system, dataClassification",
    "Standard resource group structure per solution",
    "No click-ops in production (changes via PR/pipeline)",
    "Policy baselines: platform + workload + environment",
    "Regular compliance review cadence and reporting",
  ];

  const whyImportantDa = [
    {
      title: "Azure styrer ikke sig selv",
      desc: "Hvis standarder ikke håndhæves løbende, opstår drift stille og roligt, indtil sikkerhed, omkostninger eller stabilitet bliver påvirket.",
    },
    {
      title: "Silent drift er reelt",
      desc: "Én manuel ændring kan omgå conventions og skabe langsigtet risiko som public exposure, forkerte SKUs eller manglende tags.",
    },
    {
      title: "Konsistens reducerer risiko",
      desc: "Når alle miljøer følger samme baseline, bliver fejlfinding hurtigere og incidents færre.",
    },
    {
      title: "Compliance bliver rutine",
      desc: "Governance gør compliance målbar, gentagelig og auditerbar, ikke et årligt panikprojekt.",
    },
  ];

  const useCasesDa = [
    {
      title: "Undgå public exposure",
      desc: "Bloker public IPs eller håndhæv private endpoints for følsomme workloads, før fejlen sker.",
    },
    {
      title: "Cost control og ansvarlighed",
      desc: "Kræv tags og håndhæv SKU-/regionsregler, så I undgår overraskende regninger og uejede ressourcer.",
    },
    {
      title: "Standardisér landing zones i skala",
      desc: "Anvend baselines ensartet på tværs af subscriptions og miljøer som dev, pre og prod.",
    },
    {
      title: "Sikker selv-healing hvor det giver mening",
      desc: "Auto-remediate noncompliant ressourcer og alert når remediation kræver menneskelig godkendelse.",
    },
  ];

  const conventionsDa = [
    "Tydelig naming convention per miljø og workload",
    "Obligatoriske tags: owner, costCenter, system, dataClassification",
    "Standard resource group-struktur per løsning",
    "Ingen click-ops i produktion, ændringer via PR og pipeline",
    "Policy baselines: platform, workload og miljø",
    "Fast cadence for compliance review og rapportering",
  ];

  const currentWhyImportant = isDa ? whyImportantDa : whyImportant;
  const currentUseCases = isDa ? useCasesDa : useCases;
  const currentConventions = isDa ? conventionsDa : conventions;

  return (
    <ServicePageShell>
      <div className="gov-wrap">
        {/* HERO */}
        <section className="gov-hero">
          <div className="gov-heroInner">
            <div className="gov-heroText">
              <div className="gov-pill">
                <span className="gov-dot" /> Azure Governance
              </div>

              <h1 className="gov-title">
                {isDa ? "Azure styrer ikke sig selv. Governance holder miljøet sikkert og forudsigeligt." : "Azure does not fix itself — governance keeps it secure and predictable."}
              </h1>

              <p className="gov-subtitle">
                {isDa ? "Governance er måden I undgår silent drift, inkonsistente miljøer, uventede omkostninger og sikkerhedshuller. Vi implementerer policy baselines, conventions og governance-as-code, så jeres cloud forbliver kontrolleret mens den vokser." : "Governance is how you avoid silent drift, inconsistent environments, surprise costs, and security gaps. We implement policy baselines, conventions, and “governance as code” so your cloud stays controlled as it grows."}
              </p>

              <div className="gov-ctaRow">
                <a className="gov-btnPrimary" href="/#contact-section">
                  {isDa ? "Styrk governance" : "Strengthen governance"} <span aria-hidden="true">→</span>
                </a>
                <a className="gov-btnGhost" href="/use-cases">
                  {isDa ? "Se cases" : "View use cases"}
                </a>
              </div>

              <div className="gov-trust">
                <div className="gov-trustItem">
                  <strong>{isDa ? "Sikker" : "Secure"}</strong>
                  <span>{isDa ? "Som standard" : "By default"}</span>
                </div>
                <div className="gov-divider" />
                <div className="gov-trustItem">
                  <strong>{isDa ? "Konsistent" : "Consistent"}</strong>
                  <span>Baselines</span>
                </div>
                <div className="gov-divider" />
                <div className="gov-trustItem">
                  <strong>{isDa ? "Auditerbar" : "Auditable"}</strong>
                  <span>{isDa ? "Som kode" : "As code"}</span>
                </div>
              </div>
            </div>

            <div className="gov-heroCard">
              <div className="gov-cardHead">
                <h3>{isDa ? "Hvad governance forebygger" : "What governance prevents"}</h3>
                <p>{isDa ? "Typiske 'det skete bare'-problemer vi stopper tidligt." : "Common “it just happened” problems we stop early."}</p>
              </div>

              <ul className="gov-bullets">
                <li>
                  <span className="gov-check" aria-hidden="true">
                    ✓
                  </span>
                  {isDa ? "Public endpoints oprettet ved en fejl" : "Public endpoints created by mistake"}
                </li>
                <li>
                  <span className="gov-check" aria-hidden="true">
                    ✓
                  </span>
                  {isDa ? "Manglende tags → ukendte ejere og cost chaos" : "Missing tags → unknown owners & cost chaos"}
                </li>
                <li>
                  <span className="gov-check" aria-hidden="true">
                    ✓
                  </span>
                  {isDa ? "Policy drift mellem miljøer" : "Policy drift between environments"}
                </li>
                <li>
                  <span className="gov-check" aria-hidden="true">
                    ✓
                  </span>
                  {isDa ? "Manuelle ændringer der bryder standarder" : "Manual changes that break standards"}
                </li>
              </ul>

              <div className="gov-note">
                <span className="gov-noteBadge">{isDa ? "Virkelighed" : "Reality"}</span>
                <span>
                  {isDa ? "Governance er ikke en engangsopsætning. Det er et system der holder Azure stabilt over tid." : "Governance isn’t a one-time setup — it’s a system that keeps your Azure stable over time."}
                </span>
              </div>
            </div>
          </div>

          <div className="gov-glow gov-glow1" />
          <div className="gov-glow gov-glow2" />
        </section>

        {/* WHY IMPORTANT */}
        <section className="gov-section gov-why">
          <div className="gov-sectionHead">
            <h2>{isDa ? "Hvorfor det er vigtigt" : "Why it’s so important"}</h2>
            <p>
              {isDa ? "De fleste Azure-problemer skyldes ikke dårlig teknologi, men manglende guardrails, inkonsistente standarder og silent drift." : "Most Azure problems aren’t caused by “bad technology” — they come from missing guardrails, inconsistent standards, and silent drift."}
            </p>
          </div>

          <div className="gov-whyGrid">
            {currentWhyImportant.map((w) => (
              <div key={w.title} className="gov-whyCard">
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SERVICES */}
        <section className="gov-section">
          <div className="gov-sectionHead">
            <h2>{isDa ? "Governance-services vi leverer" : "Governance services we deliver"}</h2>
            <p>
              {isDa ? "Policies, initiatives, baselines, conventions og automation, så platformen forbliver compliant og forudsigelig." : "Policies, initiatives, baselines, conventions, and automation — so the platform stays compliant and predictable."}
            </p>
          </div>

          <div className="gov-grid">
            {governanceServices.map((s) => (
              <div key={s.title} className="gov-card">
                <div className="gov-cardIcon">{s.icon}</div>
                <div className="gov-cardBody">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <ul className="gov-points">
                    {s.points.map((p) => (
                      <li key={p}>
                        <span aria-hidden="true">→</span> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* USE CASES */}
        <section className="gov-section gov-usecases">
          <div className="gov-sectionHead">
            <h2>{isDa ? "Reelle use cases" : "Real use cases"}</h2>
            <p>{isDa ? "Praktiske governance-scenarier der hurtigt giver værdi." : "Practical governance scenarios that deliver value quickly."}</p>
          </div>

          <div className="gov-useGrid">
            {currentUseCases.map((u) => (
              <div key={u.title} className="gov-useCard">
                <h3>{u.title}</h3>
                <p>{u.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CONVENTIONS */}
        <section className="gov-section gov-conv">
          <div className="gov-sectionHead">
            <h2>{isDa ? "Conventions og gode praksisser" : "Conventions & good practices"}</h2>
            <p>
              {isDa ? "Governance virker bedst når standarder er enkle, tydelige og håndhæves konsekvent." : "Governance works best when standards are simple, clear, and consistently enforced."}
            </p>
          </div>

          <div className="gov-convCard">
            <ul className="gov-convList">
              {currentConventions.map((c) => (
                <li key={c}>
                  <span className="gov-check" aria-hidden="true">
                    ✓
                  </span>
                  {c}
                </li>
              ))}
            </ul>

            <div className="gov-banner">
              <div>
                <h3>{isDa ? "Vil I have en governance baseline for jeres miljøer?" : "Want a governance baseline for your environments?"}</h3>
                <p>
                  {isDa ? "Vi kan implementere platform baseline og workload baselines, deployet via PR og pipeline, så ændringer er kontrollerede og auditerbare." : "We can implement a platform baseline and workload baselines, deployed via PR + pipeline so changes are controlled and auditable."}
                </p>
              </div>
              <a className="gov-btnPrimary" href="/#contact-section">
                {isDa ? "Byg baseline" : "Build the baseline"} <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="gov-section gov-final">
          <div className="gov-finalCard">
            <h2>{isDa ? "Klar til at stoppe drift og håndhæve standarder?" : "Ready to stop drift and enforce standards?"}</h2>
            <p>
              {isDa ? "Fortæl hvordan jeres Azure er struktureret i dag. Vi foreslår en klar governance-model med policies, initiatives, conventions og en implementeringsplan bygget til jeres virkelighed." : "Tell us how your Azure is structured today. We’ll propose a clear governance model: policies, initiatives, conventions, and an implementation plan — built for your reality."}
            </p>
            <div className="gov-ctaRow">
              <a className="gov-btnPrimary" href="/#contact-section">
                {isDa ? "Kontakt os" : "Contact us"} <span aria-hidden="true">→</span>
              </a>
              <a className="gov-btnGhost" href="/use-cases">
                {isDa ? "Se ydelser" : "Explore services"}
              </a>
            </div>
          </div>
        </section>

        {/* CSS */}
        <style>{`
          .gov-wrap{
            max-width: 1100px;
            margin: 0 auto;
            padding: 28px 18px 70px;
            font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Apple Color Emoji", "Segoe UI Emoji";
            color: #0f172a;
          }

          /* HERO */
          .gov-hero{
            position: relative;
            border-radius: 28px;
            padding: 34px 20px;
            overflow: hidden;
            background:
              radial-gradient(1200px 500px at 10% 10%, rgba(59,130,246,0.18), transparent 60%),
              radial-gradient(900px 420px at 90% 10%, rgba(168,85,247,0.16), transparent 58%),
              linear-gradient(180deg, #ffffff, #f8fafc);
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow:
              0 20px 60px rgba(2, 6, 23, 0.10),
              0 2px 12px rgba(2, 6, 23, 0.06);
          }

          .gov-heroInner{
            display: grid;
            grid-template-columns: 1.2fr 0.8fr;
            gap: 22px;
            align-items: center;
            position: relative;
            z-index: 2;
          }

          @media (max-width: 900px){
            .gov-heroInner{ grid-template-columns: 1fr; }
          }

          .gov-pill{
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 8px 12px;
            border-radius: 999px;
            background: rgba(2, 6, 23, 0.04);
            border: 1px solid rgba(15,23,42,0.08);
            font-weight: 800;
            font-size: 0.95rem;
          }

          .gov-dot{
            width: 10px; height: 10px;
            border-radius: 999px;
            background: linear-gradient(135deg, #3b82f6, #a855f7);
            box-shadow: 0 0 0 3px rgba(59,130,246,0.14);
          }

          .gov-title{
            margin: 14px 0 10px;
            font-size: clamp(2rem, 2.6vw, 3.1rem);
            line-height: 1.06;
            letter-spacing: -0.02em;
          }

          .gov-subtitle{
            margin: 0 0 18px;
            font-size: 1.08rem;
            line-height: 1.6;
            color: rgba(15, 23, 42, 0.78);
            max-width: 70ch;
          }

          .gov-ctaRow{
            display: flex;
            gap: 12px;
            flex-wrap: wrap;
            margin-bottom: 18px;
          }

          .gov-btnPrimary{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            padding: 12px 16px;
            border-radius: 14px;
            text-decoration: none;
            font-weight: 900;
            color: white;
            background: linear-gradient(135deg, #2563eb, #7c3aed);
            box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
            border: 1px solid rgba(255,255,255,0.22);
            transition: transform .15s ease, box-shadow .15s ease;
          }
          .gov-btnPrimary:hover{
            transform: translateY(-1px);
            box-shadow: 0 18px 40px rgba(37, 99, 235, 0.22);
          }

          .gov-btnGhost{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 12px 16px;
            border-radius: 14px;
            text-decoration: none;
            font-weight: 900;
            color: #0f172a;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.10);
            backdrop-filter: blur(8px);
            transition: transform .15s ease, background .15s ease;
          }
          .gov-btnGhost:hover{
            transform: translateY(-1px);
            background: rgba(255,255,255,0.95);
          }

          .gov-trust{
            display: inline-flex;
            align-items: center;
            gap: 14px;
            padding: 12px 14px;
            border-radius: 18px;
            background: rgba(255,255,255,0.70);
            border: 1px solid rgba(15,23,42,0.08);
            backdrop-filter: blur(10px);
          }
          .gov-trustItem{ display: grid; line-height: 1.1; }
          .gov-trustItem span{
            font-size: 0.9rem;
            color: rgba(15,23,42,0.7);
          }
          .gov-divider{
            width: 1px;
            height: 28px;
            background: rgba(15,23,42,0.12);
          }

          .gov-heroCard{
            border-radius: 22px;
            background: rgba(255,255,255,0.70);
            border: 1px solid rgba(15,23,42,0.10);
            box-shadow: 0 18px 50px rgba(2, 6, 23, 0.10);
            padding: 16px;
            backdrop-filter: blur(10px);
          }
          .gov-cardHead h3{
            margin: 0;
            font-size: 1.1rem;
            letter-spacing: -0.01em;
          }
          .gov-cardHead p{
            margin: 6px 0 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.5;
          }

          .gov-bullets{
            margin: 12px 0 0;
            padding: 0;
            list-style: none;
            display: grid;
            gap: 10px;
            color: rgba(15,23,42,0.78);
          }
          .gov-bullets li{
            display: grid;
            grid-template-columns: 24px 1fr;
            gap: 10px;
            align-items: start;
            padding: 12px 12px;
            border-radius: 16px;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.08);
          }

          .gov-check{
            width: 24px; height: 24px;
            border-radius: 999px;
            display: grid;
            place-items: center;
            background: rgba(37,99,235,0.12);
            border: 1px solid rgba(37,99,235,0.18);
            font-weight: 900;
            line-height: 1;
          }

          .gov-note{
            margin-top: 12px;
            padding: 12px;
            border-radius: 16px;
            background: linear-gradient(135deg, rgba(37,99,235,0.10), rgba(124,58,237,0.08));
            border: 1px solid rgba(15,23,42,0.10);
            display: flex;
            gap: 10px;
            align-items: flex-start;
            color: rgba(15,23,42,0.78);
            line-height: 1.5;
          }
          .gov-noteBadge{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            height: 24px;
            padding: 0 10px;
            border-radius: 999px;
            background: rgba(255,255,255,0.7);
            border: 1px solid rgba(15,23,42,0.08);
            font-weight: 900;
            font-size: 0.85rem;
            white-space: nowrap;
          }

          /* Decorative glows inside hero */
          .gov-glow{
            position: absolute;
            width: 320px;
            height: 320px;
            border-radius: 999px;
            filter: blur(40px);
            opacity: 0.6;
            z-index: 1;
            pointer-events: none;
          }
          .gov-glow1{
            left: -120px;
            top: -120px;
            background: rgba(59,130,246,0.35);
          }
          .gov-glow2{
            right: -120px;
            top: -120px;
            background: rgba(168,85,247,0.30);
          }

          /* SECTIONS */
          .gov-section{ margin-top: 34px; }

          .gov-sectionHead{
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            margin-bottom: 14px;
          }
          .gov-sectionHead h2{
            margin: 0;
            font-size: 1.7rem;
            letter-spacing: -0.02em;
          }
          .gov-sectionHead p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
          }
          @media (max-width: 860px){
            .gov-sectionHead{
              flex-direction: column;
              align-items: flex-start;
            }
          }

          /* WHY grid */
          .gov-whyGrid{
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
          @media (max-width: 760px){
            .gov-whyGrid{ grid-template-columns: 1fr; }
          }
          .gov-whyCard{
            border-radius: 18px;
            padding: 14px;
            background: white;
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 10px 30px rgba(2,6,23,0.05);
          }
          .gov-whyCard h3{
            margin: 0 0 6px;
            font-size: 1.02rem;
            letter-spacing: -0.01em;
          }
          .gov-whyCard p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
          }

          /* SERVICES grid (icon cards like your first page) */
          .gov-grid{
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 14px;
          }
          @media (max-width: 980px){
            .gov-grid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }
          @media (max-width: 620px){
            .gov-grid{ grid-template-columns: 1fr; }
          }

          .gov-card{
            border-radius: 20px;
            background: white;
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 10px 30px rgba(2,6,23,0.06);
            padding: 16px;
            display: grid;
            grid-template-columns: 44px 1fr;
            gap: 12px;
            transition: transform .15s ease, box-shadow .15s ease;
            min-height: 150px;
          }
          .gov-card:hover{
            transform: translateY(-2px);
            box-shadow: 0 18px 48px rgba(2,6,23,0.09);
          }

          .gov-cardIcon{
            width: 44px;
            height: 44px;
            border-radius: 14px;
            display: grid;
            place-items: center;
            background: linear-gradient(180deg, rgba(59,130,246,0.12), rgba(168,85,247,0.10));
            border: 1px solid rgba(15,23,42,0.08);
            color: #0f172a;
          }
          .icon{ width: 22px; height: 22px; }

          .gov-cardBody h3{
            margin: 0;
            font-size: 1.05rem;
            letter-spacing: -0.01em;
          }
          .gov-cardBody p{
            margin: 6px 0 10px;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
            font-size: 0.98rem;
          }

          .gov-points{
            margin: 0;
            padding: 0;
            list-style: none;
            display: grid;
            gap: 8px;
            color: rgba(15,23,42,0.78);
            line-height: 1.45;
            font-size: 0.96rem;
          }
          .gov-points li{
            display: grid;
            grid-template-columns: 18px 1fr;
            gap: 8px;
            align-items: start;
            padding: 10px 12px;
            border-radius: 16px;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.08);
          }

          /* USE CASES */
          .gov-useGrid{
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 12px;
          }
          @media (max-width: 760px){
            .gov-useGrid{ grid-template-columns: 1fr; }
          }
          .gov-useCard{
            border-radius: 18px;
            padding: 14px;
            background: white;
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 10px 30px rgba(2,6,23,0.05);
          }
          .gov-useCard h3{
            margin: 0 0 6px;
            font-size: 1.02rem;
            letter-spacing: -0.01em;
          }
          .gov-useCard p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.55;
          }

          /* CONVENTIONS */
          .gov-convCard{
            border-radius: 26px;
            padding: 22px;
            background:
              radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.10), transparent 55%),
              radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.09), transparent 55%),
              linear-gradient(180deg, #ffffff, #f8fafc);
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 18px 50px rgba(2,6,23,0.07);
          }

          .gov-convList{
            margin: 0;
            padding: 0;
            list-style: none;
            display: grid;
            gap: 10px;
          }
          .gov-convList li{
            display: grid;
            grid-template-columns: 24px 1fr;
            gap: 10px;
            align-items: start;
            padding: 12px 12px;
            border-radius: 16px;
            background: rgba(255,255,255,0.75);
            border: 1px solid rgba(15,23,42,0.08);
          }

          .gov-banner{
            margin-top: 16px;
            border-radius: 22px;
            padding: 18px;
            background: linear-gradient(135deg, rgba(37,99,235,0.12), rgba(124,58,237,0.10));
            border: 1px solid rgba(15,23,42,0.10);
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 14px;
            flex-wrap: wrap;
          }
          .gov-banner h3{
            margin: 0 0 6px;
            font-size: 1.15rem;
            letter-spacing: -0.01em;
          }
          .gov-banner p{
            margin: 0;
            color: rgba(15,23,42,0.72);
            line-height: 1.5;
            max-width: 78ch;
          }

          /* FINAL CTA */
          .gov-finalCard{
            border-radius: 26px;
            padding: 22px;
            background:
              radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.10), transparent 55%),
              radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.09), transparent 55%),
              linear-gradient(180deg, #ffffff, #f8fafc);
            border: 1px solid rgba(15,23,42,0.08);
            box-shadow: 0 18px 50px rgba(2,6,23,0.07);
          }
          .gov-finalCard h2{
            margin: 0 0 10px;
            font-size: 1.7rem;
            letter-spacing: -0.02em;
          }
          .gov-finalCard p{
            margin: 0 0 14px;
            color: rgba(15,23,42,0.72);
            line-height: 1.6;
            max-width: 78ch;
          }
        `}</style>
      </div>
    </ServicePageShell>
  );
};

export default AzureGovernance;
